/**
 * NFC Solutions — LAN Peer Cache (Session 27.77)
 * ─────────────────────────────────────────────────
 *
 * PURPOSE
 * ───────
 * Whenever a WebRTC peer connection successfully hands over to a
 * host ICE candidate (i.e. two devices are actively communicating
 * over the local LAN), we extract the peer's IPv4 address from the
 * SDP and persist it to localStorage. That gives us:
 *
 *   • A durable list of "devices we have paired with before" that
 *     survives page reloads, app restarts, and even brief power
 *     outages.
 *   • Enough information to attempt a "warm reconnect" when internet
 *     drops — the mDNS discovery pass can be short-circuited if the
 *     peer is still at the cached IP.
 *   • Diagnostic data for the Admin "LAN Discovery" panel: "You have
 *     paired with 3 devices in the last 24 hours."
 *
 * STORAGE SHAPE
 * ─────────────
 *   localStorage["nfcs:lan_peers"] = JSON.stringify({
 *     peers: {
 *       "<device_id>": {
 *         device_id, device_label, role, ipv4, port,
 *         first_seen_at, last_seen_at, seen_count,
 *         source: "webrtc" | "mdns" | "manual",
 *       },
 *       ...
 *     },
 *     schema: 1,
 *   })
 *
 * Entries expire 7 days after `last_seen_at` — restaurants rarely
 * change their LAN more than once a week, but a longer TTL would
 * pollute the cache with stale IPs from previous venue setups.
 */

const STORAGE_KEY = "nfcs:lan_peers";
const SCHEMA = 1;
const ENTRY_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function _readAll() {
  try {
    if (typeof localStorage === "undefined") return {};
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.schema !== SCHEMA || !parsed.peers) return {};
    const now = Date.now();
    const cleaned = {};
    for (const [id, entry] of Object.entries(parsed.peers)) {
      if (!entry || !entry.device_id) continue;
      const last = new Date(entry.last_seen_at || 0).getTime();
      if (Number.isFinite(last) && now - last > ENTRY_TTL_MS) continue;
      cleaned[id] = entry;
    }
    return cleaned;
  } catch (_) {
    return {};
  }
}

function _writeAll(peers) {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ schema: SCHEMA, peers }));
  } catch (_) { /* quota exceeded — best effort */ }
}

/**
 * Record (or refresh) a LAN peer we've observed. If the same device_id
 * already exists in the cache, its `seen_count` is incremented and
 * `last_seen_at` is bumped. Fields provided as `null`/`undefined` do
 * not overwrite existing values — this lets a partial observation
 * (e.g. mDNS with no port) enrich an existing record without erasing
 * data collected from a fuller source.
 */
export function recordLanPeer({
  device_id, device_label = null, role = null, ipv4 = null, port = null,
  source = "webrtc", business_id = null,
} = {}) {
  if (!device_id) return null;
  const peers = _readAll();
  const now = new Date().toISOString();
  const existing = peers[device_id] || {};
  const entry = {
    device_id,
    device_label: device_label || existing.device_label || null,
    role: role || existing.role || null,
    ipv4: ipv4 || existing.ipv4 || null,
    port: port || existing.port || null,
    first_seen_at: existing.first_seen_at || now,
    last_seen_at: now,
    seen_count: (existing.seen_count || 0) + 1,
    source: source || existing.source || "webrtc",
    // Session 30.26 — lets the offline discovery pass skip peers that
    // belong to a different business sharing the same Wi-Fi.
    business_id: business_id || existing.business_id || null,
  };
  peers[device_id] = entry;
  _writeAll(peers);
  return entry;
}

/**
 * Return every cached peer (already expiry-filtered). Sorted by
 * most-recently-seen first.
 */
export function listCachedLanPeers() {
  const peers = _readAll();
  return Object.values(peers).sort((a, b) => {
    const ta = new Date(a.last_seen_at || 0).getTime();
    const tb = new Date(b.last_seen_at || 0).getTime();
    return tb - ta;
  });
}

/**
 * Delete a single peer from the cache. Used when the peer explicitly
 * disconnects (WebRTC `iceConnectionState === "closed"`).
 */
export function forgetLanPeer(device_id) {
  if (!device_id) return;
  const peers = _readAll();
  if (!peers[device_id]) return;
  delete peers[device_id];
  _writeAll(peers);
}

/**
 * Clear every cached peer. Used by the "Rescan LAN" admin action and
 * on logout.
 */
export function forgetAllLanPeers() {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.removeItem(STORAGE_KEY);
  } catch (_) { /* noop */ }
}

/**
 * Extract host/reflexive IPv4 candidates from a raw SDP string. We
 * ignore .local hostnames (mDNS-anonymised candidates), IPv6, and
 * relay candidates. Returns the FIRST usable IPv4 found, or null.
 */
export function extractIpv4FromSdp(sdp) {
  if (typeof sdp !== "string" || !sdp) return null;
  const CANDIDATE_LINE = /a=candidate:[^\r\n]+/g;
  const IPV4 = /\b(25[0-5]|2[0-4]\d|1?\d{1,2})(\.(25[0-5]|2[0-4]\d|1?\d{1,2})){3}\b/;
  const lines = sdp.match(CANDIDATE_LINE) || [];
  // Prefer host candidates (typ host) over srflx/prflx. If none, take
  // whichever v4 candidate we can find (excluding relay which needs
  // internet).
  for (const line of lines) {
    if (line.includes("typ host")) {
      const m = line.match(IPV4);
      if (m && !m[0].startsWith("0.")) return m[0];
    }
  }
  for (const line of lines) {
    if (line.includes("typ relay")) continue;
    const m = line.match(IPV4);
    if (m && !m[0].startsWith("0.")) return m[0];
  }
  return null;
}

/**
 * Convenience: given an RTCPeerConnection with a completed handshake,
 * extract the remote peer's IPv4 (via `remoteDescription.sdp`) and
 * record it. Safe to call multiple times — subsequent calls just
 * refresh `last_seen_at`.
 */
export function recordFromRtcPeerConnection(pc, { device_id, device_label, role, business_id = null } = {}) {
  if (!pc || !device_id) return null;
  try {
    const sdp = pc.remoteDescription && pc.remoteDescription.sdp;
    if (!sdp) return null;
    const ipv4 = extractIpv4FromSdp(sdp);
    return recordLanPeer({ device_id, device_label, role, ipv4, source: "webrtc", business_id });
  } catch (_) {
    return null;
  }
}
